// Isomorphic language resolver. Runs on the server (SSR / prerender) and on
// the client (subsequent SPA navigations). Both paths must return the same
// value on first render so `useSuspenseQuery(catalogQuery(lang))` hits the
// same cache key the loader primed — no double fetch, no language flicker.
//
// The server implementation is provided via `createIsomorphicFn().server(...)`
// so its body (and any transitive server-only imports) is stripped from the
// client bundle by the TanStack Start build.
import { createIsomorphicFn } from "@tanstack/react-start";
import { type AppLang, SUPPORTED_LANGS, normalizeLang } from "@/i18n";

const SUPPORTED = new Set<string>(SUPPORTED_LANGS as readonly string[]);

function readClientLang(): AppLang {
  if (typeof window === "undefined") return "es";
  try {
    const stored = window.localStorage.getItem("mm.lang");
    if (stored) {
      const lng = stored.slice(0, 2).toLowerCase();
      if (SUPPORTED.has(lng)) return lng as AppLang;
    }
  } catch {
    /* ignore */
  }
  const htmlLang = document.documentElement.lang?.slice(0, 2).toLowerCase();
  if (htmlLang && SUPPORTED.has(htmlLang)) return htmlLang as AppLang;
  const nav = navigator.language?.slice(0, 2).toLowerCase();
  if (nav && SUPPORTED.has(nav)) return nav as AppLang;
  return "es";
}

/**
 * Isomorphic. On the server, resolves from cookies/headers; on the client,
 * from localStorage / <html lang> / navigator.language. Falls back to "es".
 */
export const resolveActiveLangAsync = createIsomorphicFn()
  .client((): AppLang => readClientLang())
  .server(async (): Promise<AppLang> => {
    const mod = await import("./request-lang.server");
    return mod.resolveRequestLang();
  });

/**
 * Sync client-only. Safe to call from components and queryFn factories that
 * run in the browser. Returns "es" on the server (loaders should use the
 * isomorphic variant when they need the server-derived value).
 */
export function resolveActiveLang(): AppLang {
  return readClientLang();
}

export { normalizeLang };
